
import React from 'react'
import { Calendar, Clock, CheckCircle, AlertTriangle } from 'lucide-react'

interface GanttChartProps {
  etapas: any[]
  dataInicio: string
  dataFim: string
}

export const GanttChart: React.FC<GanttChartProps> = ({
  etapas,
  dataInicio,
  dataFim
}) => {
  const startDate = new Date(dataInicio)
  const endDate = new Date(dataFim)
  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

  // Gerar array de meses para o cabeçalho
  const generateMonths = () => {
    const months = []
    const current = new Date(startDate)
    
    while (current <= endDate) {
      months.push({
        month: current.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }),
        date: new Date(current)
      })
      current.setMonth(current.getMonth() + 1)
    }
    
    return months
  }

  const months = generateMonths()

  // Calcular posição e largura da barra para cada etapa
  const getBarPosition = (etapa: any) => {
    if (!etapa.dataInicioPrevisto || !etapa.dataTerminoPrevisto) {
      return { left: 0, width: 0, visible: false }
    }

    const etapaStart = new Date(etapa.dataInicioPrevisto)
    const etapaEnd = new Date(etapa.dataTerminoPrevisto)
    
    const daysFromStart = Math.ceil((etapaStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    const etapaDuration = Math.ceil((etapaEnd.getTime() - etapaStart.getTime()) / (1000 * 60 * 60 * 24))
    
    const left = Math.max(0, (daysFromStart / totalDays) * 100)
    const width = Math.min(100 - left, (etapaDuration / totalDays) * 100)
    
    return { left, width, visible: width > 0 }
  }

  // Calcular posição da barra de progresso real
  const getRealProgressBar = (etapa: any) => {
    if (!etapa.dataInicioReal) {
      return { left: 0, width: 0, visible: false }
    }

    const etapaStart = new Date(etapa.dataInicioReal)
    let etapaEnd = new Date()
    
    if (etapa.dataTerminoReal) {
      etapaEnd = new Date(etapa.dataTerminoReal)
    }
    
    const daysFromStart = Math.ceil((etapaStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    const etapaDuration = Math.ceil((etapaEnd.getTime() - etapaStart.getTime()) / (1000 * 60 * 60 * 24))
    
    const left = Math.max(0, (daysFromStart / totalDays) * 100)
    const width = Math.min(100 - left, (etapaDuration / totalDays) * 100)
    
    return { left, width, visible: width > 0 }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'previsto':
        return 'bg-gray-400'
      case 'em_andamento':
        return 'bg-blue-500'
      case 'realizado':
        return 'bg-green-500'
      case 'atrasado':
        return 'bg-red-500'
      default:
        return 'bg-gray-400'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'previsto':
        return <Clock size={14} className="text-gray-500" />
      case 'em_andamento':
        return <Calendar size={14} className="text-blue-500" />
      case 'realizado':
        return <CheckCircle size={14} className="text-green-500" />
      case 'atrasado':
        return <AlertTriangle size={14} className="text-red-500" />
      default:
        return <Clock size={14} className="text-gray-500" />
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900">Cronograma Gantt</h3>
        <p className="text-sm text-gray-600">
          Período: {formatDate(dataInicio)} até {formatDate(dataFim)} ({totalDays} dias)
        </p>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Cabeçalho dos meses */}
          <div className="flex border-b bg-gray-50">
            <div className="w-80 p-3 border-r font-medium text-gray-700">
              Etapas
            </div>
            <div className="flex-1 flex">
              {months.map((month, index) => (
                <div
                  key={index}
                  className="flex-1 p-3 border-r text-center text-sm font-medium text-gray-700"
                >
                  {month.month}
                </div>
              ))}
            </div>
          </div>

          {/* Linhas das etapas */}
          {etapas.map((etapa, index) => {
            const plannedBar = getBarPosition(etapa)
            const realBar = getRealProgressBar(etapa)

            return (
              <div key={etapa.id || index} className="flex border-b hover:bg-gray-50">
                {/* Informações da etapa */}
                <div className="w-80 p-3 border-r">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusIcon(etapa.status)}
                    <span className="font-medium text-gray-900">{etapa.nome}</span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div>Previsto: {formatDate(etapa.dataInicioPrevisto)} - {formatDate(etapa.dataTerminoPrevisto)}</div>
                    {etapa.dataInicioReal && (
                      <div>Real: {formatDate(etapa.dataInicioReal)} - {formatDate(etapa.dataTerminoReal)}</div>
                    )}
                    <div>Orçado: {formatCurrency(etapa.valorOrcado || 0)}</div>
                    {etapa.valorRealizado > 0 && (
                      <div>Realizado: {formatCurrency(etapa.valorRealizado)}</div>
                    )}
                  </div>
                </div>

                {/* Área do gráfico */}
                <div className="flex-1 p-3 relative" style={{ minHeight: '80px' }}>
                  {/* Barra planejada */}
                  {plannedBar.visible && (
                    <div
                      className={`absolute top-4 h-4 ${getStatusColor(etapa.status)} rounded opacity-60`}
                      style={{
                        left: `${plannedBar.left}%`,
                        width: `${plannedBar.width}%`
                      }}
                      title={`Planejado: ${formatDate(etapa.dataInicioPrevisto)} - ${formatDate(etapa.dataTerminoPrevisto)}`}
                    />
                  )}

                  {/* Barra real */}
                  {realBar.visible && (
                    <div
                      className="absolute top-6 h-4 bg-gray-800 rounded"
                      style={{
                        left: `${realBar.left}%`,
                        width: `${realBar.width}%`
                      }}
                      title={`Real: ${formatDate(etapa.dataInicioReal)} - ${etapa.dataTerminoReal ? formatDate(etapa.dataTerminoReal) : 'Em andamento'}`}
                    />
                  )}

                  {/* Linha do hoje */}
                  {(() => {
                    const today = new Date()
                    const daysFromStart = Math.ceil((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
                    const todayPosition = (daysFromStart / totalDays) * 100

                    if (todayPosition >= 0 && todayPosition <= 100) {
                      return (
                        <div
                          className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                          style={{ left: `${todayPosition}%` }}
                          title="Hoje"
                        />
                      )
                    }
                    return null
                  })()}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Legenda */}
      <div className="p-4 border-t bg-gray-50">
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 bg-gray-400 rounded opacity-60"></div>
            <span>Planejado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-3 bg-gray-800 rounded"></div>
            <span>Real</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-0.5 h-4 bg-red-500"></div>
            <span>Hoje</span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <div className="flex items-center gap-1">
              <Clock size={14} className="text-gray-500" />
              <span>Previsto</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-blue-500" />
              <span>Em Andamento</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle size={14} className="text-green-500" />
              <span>Realizado</span>
            </div>
            <div className="flex items-center gap-1">
              <AlertTriangle size={14} className="text-red-500" />
              <span>Atrasado</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GanttChart
